import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { errorMessage, monthName, recordedLabel } from "./format";
import {
  dotPosition,
  positionLabel,
  pressureVerdict,
  teamAveragePositions,
  teamCountsLabel,
  teamFactorSummaries,
  type MarketFactor,
  type TeamPositionGroup,
} from "./marketViewLogic";
import type { MarketTeamViewData } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  factors: MarketFactor[];
  refreshCount: number;
}

interface LoadedTeam {
  loadKey: string;
  team: MarketTeamViewData;
}

const EMPTY_TEXT_CLASSES = "m-0 text-rs-light-gray text-[13px]";

function consultantCountLabel(count: number): string {
  return `${count} consultant${count === 1 ? "" : "s"}`;
}

export function TeamView({ month, resinFamilyCode, factors, refreshCount }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const { appUserId } = useAppUser();
  const loadKey = `${resinFamilyCode}|${month}|${refreshCount}`;
  const [loadedTeam, setLoadedTeam] = useState<LoadedTeam | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(
        `consultant-cockpit/market-views/team?resinFamily=${encodeURIComponent(resinFamilyCode)}&month=${month}-01`,
      )
      .then((response) => response.json())
      .then((team: MarketTeamViewData) => {
        if (isStale) return;
        setLoadedTeam({ loadKey, team });
        setLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, loadKey]);

  const team = loadedTeam?.loadKey === loadKey ? loadedTeam.team : null;
  const views = useMemo(() => team?.views ?? [], [team]);
  const summaries = useMemo(() => teamFactorSummaries(factors, views), [factors, views]);
  const verdict = pressureVerdict(teamAveragePositions(summaries));
  const monthLabel = monthName(month);

  return (
    <Card className="mt-4">
      <div className="flex flex-wrap items-center gap-2.5 mb-2.5">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-[15px] font-semibold text-white cursor-pointer hover:text-rs-green"
          aria-expanded={isOpen}
          aria-controls="team-view-chart"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        >
          <span className="material-symbols-sharp text-[18px]" aria-hidden="true">
            {isOpen ? "expand_less" : "expand_more"}
          </span>
          Team view
        </button>
        {team && <Chip>{consultantCountLabel(views.length)}</Chip>}
        {verdict && (
          <Chip variant="ok" className="font-semibold">
            {verdict.lean}
          </Chip>
        )}
      </div>

      {loadError ? (
        <p className={EMPTY_TEXT_CLASSES}>Couldn't load the team view: {loadError}</p>
      ) : !team ? (
        <p className={EMPTY_TEXT_CLASSES}>Loading…</p>
      ) : views.length === 0 ? (
        <p className={EMPTY_TEXT_CLASSES}>
          No consultant has saved a {monthLabel} {resinFamilyCode} market view yet.
        </p>
      ) : (
        <>
          <p className={`${EMPTY_TEXT_CLASSES} mb-3`}>
            {verdict
              ? `Team average of ${consultantCountLabel(views.length)}: ${verdict.buyerCount} of ${verdict.setCount} factors favor buyers, ${verdict.sellerCount} favor sellers.`
              : `${consultantCountLabel(views.length)} saved a view with no factor positions.`}
          </p>
          {isOpen && (
            <div id="team-view-chart">
              <div className="grid grid-cols-[140px_1fr_150px] gap-3 mb-1.5 text-[11px] uppercase tracking-[0.06em] text-rs-light-gray">
                <span />
                <div className="flex justify-between">
                  <span>Favors sellers</span>
                  <span>Neutral</span>
                  <span>Favors buyers</span>
                </div>
                <span />
              </div>
              {summaries.map((summary) => (
                <div
                  key={summary.factor.code}
                  className="grid grid-cols-[140px_1fr_150px] gap-3 items-center py-[7px] border-t border-rs-jet text-[13px]"
                >
                  <span title={summary.factor.hint}>{summary.factor.name}</span>
                  <div
                    className="relative h-[18px]"
                    role="img"
                    aria-label={`${summary.factor.name}: ${
                      summary.groups.length === 0 ? "not set by anyone" : teamCountsLabel(summary)
                    }`}
                  >
                    <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-linear-to-r from-rs-red via-rs-jet-light to-rs-green opacity-60" />
                    <span className="absolute left-1/2 top-0.5 bottom-0.5 w-px bg-rs-jet-light" />
                    {summary.averageScore !== undefined && (
                      <span
                        className="absolute top-0 bottom-0 w-1 -translate-x-1/2 rounded-sm bg-rs-green"
                        style={{ left: dotPosition(summary.averageScore) }}
                      />
                    )}
                    {summary.groups.map((group) => (
                      <TeamDot key={group.score} group={group} appUserId={appUserId} />
                    ))}
                  </div>
                  <span className="text-xs text-rs-light-gray">
                    {summary.groups.length === 0 ? "Not set" : teamCountsLabel(summary)}
                  </span>
                </div>
              ))}
              <div className="flex flex-wrap gap-4 mt-2.5 text-xs text-rs-light-gray">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-1 h-3 rounded-sm bg-rs-green" />
                  Team average
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-white" />
                  You
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-rs-light-purple" />
                  Other consultants
                </span>
              </div>

              <h4 className="mt-5 mb-2 text-[13px] text-white">One-line views</h4>
              <ul className="list-none m-0 p-0 flex flex-col gap-2">
                {views.map((view) => (
                  <li key={view.enteredByAppUserId} className="pb-2 border-b border-rs-jet text-[13.5px]">
                    <b className="text-white">
                      {view.enteredByDisplayName}
                      {view.enteredByAppUserId === appUserId ? " (you)" : ""}
                    </b>
                    : {view.summary || "No one-line view saved."}
                    <small className="block text-rs-light-gray">Saved {recordedLabel(view.recordedUtc)}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

interface TeamDotProps {
  group: TeamPositionGroup;
  appUserId: number | null;
}

function TeamDot({ group, appUserId }: TeamDotProps) {
  const includesMe = group.consultants.some((consultant) => consultant.appUserId === appUserId);
  const consultantCount = group.consultants.length;
  return (
    <span
      className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 p-1 cursor-default"
      style={{ left: dotPosition(group.score) }}
    >
      <span
        className={`flex items-center justify-center rounded-full text-[10px] font-semibold text-rs-black ${
          consultantCount > 1 ? "w-4 h-4" : "w-3 h-3"
        } ${includesMe ? "bg-white shadow-[0_0_0_3px_var(--color-rs-off-black)]" : "bg-rs-light-purple"}`}
      >
        {consultantCount > 1 ? consultantCount : ""}
      </span>
      <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 w-max max-w-72 -translate-x-1/2 rounded border border-rs-jet-light bg-rs-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100">
        <span className="block whitespace-nowrap">
          {positionLabel(group.score)} · {consultantCountLabel(consultantCount)}
        </span>
        {group.consultants.map((consultant) => (
          <span key={consultant.appUserId} className="block mt-0.5">
            {consultant.name}
            {consultant.appUserId === appUserId ? " (you)" : ""}
            {consultant.note && <span className="block text-rs-light-gray">{consultant.note}</span>}
          </span>
        ))}
      </span>
    </span>
  );
}
